import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getDashboardOverview,
  getDashboardRecentActivity,
  getDashboardQuickMetrics,
  getDashboardMonthlyTrend,
  DashboardOverview,
  DashboardQuickMetrics,
  DashboardMonthlyTrend,
  DashboardActivity,
} from "../../lib/api";
import { getBackendErrorMessage } from "../../lib/utils";

const RECENT_LIMIT = 5;

export function useDashboardData() {
  const query = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const [overviewData, activityData, metricsData, trendData] = await Promise.all([
        getDashboardOverview(),
        getDashboardRecentActivity(),
        getDashboardQuickMetrics(),
        getDashboardMonthlyTrend(),
      ]);
      return {
        overview: overviewData as DashboardOverview,
        recentActivity: (activityData.activities || []) as DashboardActivity[],
        quickMetrics: metricsData as DashboardQuickMetrics,
        monthlyTrend: trendData as DashboardMonthlyTrend,
      };
    },
  });

  const recentActivity = useMemo(
    () => query.data?.recentActivity ?? [],
    [query.data],
  );

  const recentByType = useMemo(() => {
    const pick = (type: DashboardActivity["type"]) =>
      recentActivity.filter((activity) => activity.type === type).slice(0, RECENT_LIMIT);
    return {
      quotations: pick("quotation"),
      invoices: pick("invoice"),
      movements: pick("movement"),
    };
  }, [recentActivity]);

  return {
    query,
    overview: query.data?.overview ?? null,
    monthlyTrend: query.data?.monthlyTrend ?? null,
    recentActivity,
    recentByType,
    error: query.error
      ? getBackendErrorMessage(query.error, "Failed to load dashboard data")
      : null,
  };
}
